import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Logo from "../Images/new_cropped_logo.png";

import {
  BiHomeAlt,
  BiBuilding,
  BiFile,
  BiBarChartSquare,
  BiShieldQuarter,
  BiMoney,
  BiLineChart,
  BiFolder,
  BiPaperPlane
} from "react-icons/bi";

function Navbar() {
  const [services, setServices] = useState([]);
  const [serviceItems, setServiceItems] = useState({});
  const [activeDropdown, setActiveDropdown] = useState(null);

  // SLUG FUNCTION
  const slugify = (text) => {
    return text
      ?.toLowerCase()
      ?.trim()
      ?.replace(/&/g, "and")
      ?.replace(/[^\w\s-]/g, "")
      ?.replace(/\s+/g, "-");
  };

  // GET SERVICE TYPES
  const getServiceTypes = async () => {
    try {
      const { data } = await axios.get(
        "https://api.compliancekare.com/api/Compliance/GetServiceTypes"
      );
      setServices(data.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getServiceTypes();
  }, []);

  const getServicesByTypeId = async (id) => {
    if (serviceItems[id]) return;
    try {
      const { data } = await axios.get(
        `https://api.compliancekare.com/api/Compliance/ServicesByTypeId?ServiceTypeId=${id}`
      );
      setServiceItems((prev) => ({
        ...prev,
        [id]: data.data || [],
      }));
    } catch (error) {
      console.error(error);
    }
  };

  // DROPDOWN
  const handleDropdownOpen = (id) => {
    setActiveDropdown(id);
    getServicesByTypeId(id);
  };
  const handleDropdownClose = () => {
    setActiveDropdown(null);
  };


  // ICON MAPPING
  const getIconForService = (name) => {
    if (!name) return <BiFolder className="nav-icon" />;
    const n = name.toLowerCase();
    if (n.includes('business')) return <BiBuilding className="nav-icon" />;
    if (n.includes('income tax') || n.includes('itr')) return <BiFile className="nav-icon" />;
    if (n.includes('gst')) return <BiBarChartSquare className="nav-icon" />;
    if (n.includes('compliance')) return <BiShieldQuarter className="nav-icon" />;
    if (n.includes('loan')) return <BiMoney className="nav-icon" />;
    if (n.includes('investment')) return <BiLineChart className="nav-icon" />;
    return <BiFolder className="nav-icon" />;
  };

  // DYNAMIC ROUTE MAPPING
  const getLinkPath = (serviceTypeName, item) => {
    const slug = slugify(item.serviceName);
    if (!serviceTypeName) return `/service/${slug}`;
    const n = serviceTypeName.toLowerCase();
    if (n.includes('income tax') || n.includes('itr')) return `/incometax/${slug}`;
    if (n.includes('gst')) return `/gst/${slug}`;
    if (n.includes('compliance')) return `/compliance/${slug}`;
    if (n.includes('loan')) return `/loan/${slug}`;
    if (n.includes('investment')) return `/investment/${slug}`;
    // Default (e.g., New Business)
    return `/service/${slug}`;
  };

  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 40) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // CLICK OUTSIDE TO CLOSE DROPDOWN
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".nav-item.dropdown")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <>
      {isSticky && <div className="navbar-placeholder" style={{ height: '90px', width: '100%' }}></div>}

      <div className={`navbar-pill-container ${isSticky ? 'scrolled-sticky' : ''}`}>
        <nav className="navbar navbar-expand-lg navbar-pill">
          <div className="container-fluid px-2 px-lg-3">

            {/* LOGO */}
            <Link to="/" className="navbar-brand m-0 p-0 header-logo-wrapper">
              <img src={Logo} alt="ComplianceKare Logo" className="logo-img" />
            </Link>

            {/* TOGGLE BUTTON */}
            <button
              className="navbar-toggler border-0 shadow-none"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#nav"
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            {/* MENU */}
            <div className="collapse navbar-collapse" id="nav">
              <ul className="navbar-nav">

                {/* HOME */}
                <li className="nav-item">
                  <Link to="/" className="nav-link custom-nav-link active-link">
                    <BiHomeAlt className="nav-icon" /> <span>Home</span>
                  </Link>
                </li>

                {/* SERVICES DYNAMIC */}
                {services.map((service) => (
                  <li
                    key={service.id}
                    className="nav-item dropdown"
                    onMouseEnter={() => {
                      if (window.innerWidth > 991) {
                        handleDropdownOpen(service.id);
                      }
                    }}
                    onMouseLeave={() => {
                      if (window.innerWidth > 991) {
                        handleDropdownClose();
                      }
                    }}
                  >
                    <span
                      className="nav-link dropdown-toggle custom-nav-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeDropdown === service.id) {
                          handleDropdownClose();
                        } else {
                          handleDropdownOpen(service.id);
                        }
                      }}
                    >
                      {getIconForService(service.serviceTypeName)}
                      <span>{service.serviceTypeName}</span>
                    </span>

                    {/* DROPDOWN MENU */}
                    <ul
                      className={`dropdown-menu custom-dropdown-menu ${activeDropdown === service.id ? "show" : ""
                        }`}
                    >
                      {serviceItems[service.id]?.length > 0 ? (
                        serviceItems[service.id].map((item) => (
                          <li key={item.id}>
                            <Link
                              className="dropdown-item custom-dropdown-item"
                              to={getLinkPath(service.serviceTypeName, item)}
                              state={{ id: item.id, serviceName: item.serviceName }}
                              onClick={handleDropdownClose}
                            >
                              {item.serviceName}
                            </Link>
                          </li>
                        ))
                      ) : (
                        <li className="dropdown-item text-muted px-3 py-2">Loading...</li>
                      )}
                    </ul>
                  </li>
                ))}
              </ul>

              {/* CONTACT BUTTON */}
              <div className="header-contact-wrapper mt-3 mt-lg-0">
                <Link to="/contact" className="btn btn-contact-pill">
                  <BiPaperPlane className="me-2 contact-icon" /> Contact
                </Link>
              </div>
            </div>
          </div>
        </nav>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .navbar-pill-container {
          position: relative;
          width: 100%;
          padding: 10px 15px;
          z-index: 9999;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          pointer-events: none;
          transform: translateZ(0);
          transition: all 0.3s ease;
        }

        .scrolled-sticky {
          position: fixed;
          top: 0;
          left: 0;
          box-shadow: 0 5px 30px rgba(0,0,0,0.05);
          animation: slideDown 0.4s ease forwards;
        }

        @keyframes slideDown {
          from { transform: translateY(-100%); }
          to { transform: translateY(0); }
        }
        
        .navbar-pill {
          position: relative;
          z-index: 9999;
          background: #ffffff;
          border-radius: 50px;
          padding: 5px 15px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.08);
          max-width: 1400px;
          margin: 0 auto;
          pointer-events: auto;
          border: 1px solid rgba(255,255,255,0.8);
          min-height: 80px;
          display: flex;
          align-items: center;
        }

        .header-logo-wrapper {
          display: flex;
          align-items: center;
          flex-shrink: 0;
          margin-right: 15px;
        }
        
        .logo-img {
          height: 55px;
          max-height: 100%;
          max-width: 100%;
          width: auto;
          object-fit: contain;
        }

        .custom-nav-link {
          color: #0f172a;
          font-weight: 700;
          font-size: 14px;
          display: flex;
          align-items: center;
          padding: 8px 14px !important;
          border-radius: 30px;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .custom-nav-link:hover {
          color: #165986;
          background: #f8fafc;
        }

        .nav-icon {
          color: #165986;
          font-size: 18px;
          margin-right: 6px;
          transition: color 0.3s ease;
        }

        .custom-nav-link:hover .nav-icon {
          color: #1e3a8a;
        }

        .active-link {
          background: #eff6ff;
          color: #165986 !important;
          border-bottom: 2px solid #3b82f6;
          border-radius: 20px;
        }

        .active-link .nav-icon {
          color: #165986;
        }
        
        /* Dropdown arrow alignment */
        .dropdown-toggle::after {
          vertical-align: middle;
          margin-left: 6px;
        }

        .custom-dropdown-menu {
          position: absolute;
          z-index: 99999;
          border: none;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          border-radius: 16px;
          padding: 12px 0;
          margin-top: 15px;
          min-width: 280px;
          animation: slideUp 0.3s ease forwards;
        }

        .custom-dropdown-menu.show {
          display: block !important;
        }

        .custom-dropdown-menu::before {
          content: "";
          position: absolute;
          top: -25px;
          left: 0;
          width: 100%;
          height: 25px;
          background: transparent;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .custom-dropdown-item {
          padding: 10px 20px;
          font-weight: 500;
          font-size: 14px;
          color: #334155;
          transition: all 0.2s ease;
          display: block;
          text-decoration: none;
        }

        .custom-dropdown-item:hover {
          background: #eff6ff;
          color: #165986;
          padding-left: 25px;
        }

        .btn-contact-pill {
          background: #165986;
          color: #fff !important;
          border-radius: 30px;
          padding: 10px 24px;
          font-weight: 700;
          font-size: 14px;
          display: inline-flex;
          align-items: center;
          transition: all 0.3s ease;
          border: 2px solid transparent;
          white-space: nowrap;
        }

        .btn-contact-pill:hover {
          background: #1e3a8a;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(30, 58, 138, 0.3);
        }

        .contact-icon {
          font-size: 14px;
        }

        /* Desktop Layout Flex Rules */
        @media (min-width: 992px) {
          .navbar-pill > .container-fluid {
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            height: 100%;
          }
          
          .navbar-collapse {
            display: flex !important;
            flex-grow: 1;
            align-items: center;
            min-width: 0; 
            justify-content: space-between;
          }
          
          .navbar-nav {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 15px;
            min-width: 0;
            margin-left: auto;
            margin-right: 15px;
            justify-content: flex-end;
            flex-shrink: 1;
          }
          
          .nav-item {
            flex-shrink: 1;
            white-space: nowrap;
          }
          
          .header-contact-wrapper {
            flex-shrink: 0;
            margin-left: 0;
          }
        }

        /* Responsive scaling for navigation at tighter desktop widths / high zoom */
        @media (min-width: 992px) and (max-width: 1350px) {
          .navbar-nav {
            gap: 6px;
          }
          .custom-nav-link {
            font-size: 13px;
            padding: 6px 10px !important;
          }
          .nav-icon {
            font-size: 16px;
            margin-right: 4px;
          }
          .btn-contact-pill {
            padding: 8px 18px;
            font-size: 13px;
          }
        }
        
        @media (min-width: 992px) and (max-width: 1150px) {
           .custom-nav-link {
            font-size: 12px;
            padding: 4px 6px !important;
          }
          .logo-img {
            height: 50px;
          }
          .navbar-nav {
            gap: 2px;
          }
        }

        /* Mobile specific styles */
        @media (max-width: 991px) {
          .navbar-pill {
            border-radius: 20px;
            padding: 10px 15px;
            min-height: auto;
          }
          .custom-dropdown-menu {
            box-shadow: none;
            background: #f8fafc;
            margin-top: 5px;
            position: relative;
          }
          .header-contact-wrapper {
            margin-bottom: 10px;
          }
        }
      `}} />
    </>
  );
}

export default Navbar;